import { createSelector } from 'reselect';
import Immutable from 'immutable';
import get from 'lodash/get';

import { Session, Message, User, Company, Form, SatisfactionForm } from 'models';

const getAppRoute = state => state.getIn(['app', 'route']);
const getChatRoute = state => state.getIn(['chat', 'route']);
const getSessionList = state => state.getIn(['chat', 'sessions'], Immutable.OrderedSet());
export const getSessionEntities = state => state.getIn(['chat', 'entities', 'sessions'], Immutable.Map());
const getCompanyEntity = state => state.getIn(['chat', 'entities', 'company'], Company());
const getUserEntities = state => state.getIn(['chat', 'entities', 'users'], Immutable.Map());
const getMessageEntities = state => state.getIn(['chat', 'entities', 'messages'], Immutable.Map());
export const getAttachmentEntities = state => state.getIn(['chat', 'entities', 'attachments'], Immutable.Map());
export const getQueueEntities = state => state.getIn(['chat', 'entities', 'sessionQueues'], Immutable.Map());
const getVolumeControlSessions = state => state.getIn(['chat', 'volumeControlSessions'], Immutable.List());
export const getScrollToMessageId = state => state.getIn(['chat', 'scrollToMessageId']);
export const getActiveSessionId = state => state.getIn(['chat', 'activeSessionId']);
export const getLastActivity = state => state.getIn(['chat', 'lastActivity']);
export const getSocketActivity = state => state.getIn(['chat', 'socketActivity']);
export const getFocus = state => state.getIn(['chat', 'focus']);
export const getIsCampaignClicked = state => state.getIn(['chat', 'isCampaignClicked']);
const getUserFromMessageProp = (state, props) => {
  const message = props.message || Message();
  return state.getIn(['chat', 'entities', 'users', message.sentBy.id], User());
};

export const getSessionIsInVolumeControl = (state, sessionId) => getVolumeControlSessions(state).includes(sessionId);

const getSessionFromPropItemId = (state, props) => {
  const item = props.item || Session();
  return state.getIn(['chat', 'entities', 'sessions', item.id], Session());
};

export const makeGetSessionBySessionId = () => {
  return createSelector(
    [getSessionEntities, (_, sessionId) => sessionId],
    (sessions, sessionId) => {
      return sessions.get(sessionId);
    },
  );
};

export const getActiveSessionIsInVolumeControl = createSelector(
  getVolumeControlSessions,
  getActiveSessionId,
  (volumeControlSessions, sessionId) => {
    return volumeControlSessions.includes(sessionId);
  },
);

export const selectVolumeControlFollowupChannelOptions = state => {
  const settings = state.getIn(['chat', 'entities', 'company', 'volumeControl'], {});
  let channels = (settings.followUpChannels && settings.followUpChannels.slice()) || [];
  if (!settings.hideWaitOption) channels = channels.concat(['wait']);
  return channels;
};

export const isSessionLocked = (state, sessionId) => {
  return Boolean(state.getIn(['chat', 'entities', 'sessions', sessionId, 'lockedAt'], false));
};

const getSessionMessagesFromPropItemId = (state, props) => {
  const item = props.item || Session();
  const sessionId = item.get('id');
  return state.getIn(['chat', 'sessions', sessionId, 'messages'], Immutable.List());
};

export const getCompany = createSelector(
  [getCompanyEntity],
  company => company,
);

export const getCompanyHistorySetting = createSelector(
  getCompanyEntity,
  company => !!company.history,
);

export const getCompanyIconUrl = createSelector(
  [getCompany],
  company => (company ? company.iconUrl : ''),
);

export const getCompanyAvatarUrl = createSelector(
  [getCompany],
  company => (company ? company.avatarUrl : ''),
);

export const getCompanyIconColor = createSelector(
  [getCompany],
  company => (company ? company.iconColor : ''),
);

export const getCompanyShowBrandingIdentifier = createSelector(
  [getCompany],
  company => (company ? company.showBrandingIdentifier : true),
);

export const chatConversationIsOpen = createSelector(
  [getAppRoute, getChatRoute],
  (appRoute, chatRoute) => appRoute === 'app' && chatRoute === 'conversation',
);

export const getCompanyClosableChatEnabled = createSelector(
  getCompany,
  company => company.closableChat,
);

export const getCompanyShowTypingIndicatorWeb = createSelector(
  getCompany,
  company => company.showTypingIndicatorWeb,
);

export const getCompanyShowTypingIndicatorCustomerWeb = createSelector(
  getCompany,
  company => company.showTypingIndicatorCustomerWeb,
);

export const getCompanyShowEmailInputBanner = createSelector(
  getCompany,
  company => company.showEmailInputBanner,
);

export const getCurrentChatRoute = createSelector(
  [getChatRoute],
  route => route,
);

export const getSessionsWithoutMessages = createSelector(
  [getSessionList, getSessionEntities],
  (sessionList, sessionEntities) =>
    sessionList
      .map(sessionId => sessionEntities.get(sessionId))
      .filter(session => session.messages.size !== 0)
      .toOrderedSet(),
);

export const getLastSeenTimeInSessionID = createSelector(
  [(_, sessionId) => sessionId, getSessionEntities],
  (sessionId, sessionEntities) => {
    return sessionEntities.get(sessionId, Session()).lastSeenAt;
  },
);

export const getAllChatSessions = createSelector(
  [getSessionsWithoutMessages],
  sessions => sessions,
);

export const getSortedOpenChatSessions = createSelector(
  [getAllChatSessions, getCompanyClosableChatEnabled],
  (sessions, closableEnabled) => {
    if (closableEnabled) {
      const openSessionsNotLocked = sessions.filter(session => !session.lockedAt);
      const openSessions = openSessionsNotLocked.sort((a, b) => b.lastMessageAt - a.lastMessageAt); // descending order
      const closedSessions = sessions.filter(session => session.lockedAt).sortBy(session => session.lockedAt);

      return openSessions.concat(closedSessions);
    }
    return sessions.sort((a, b) => b.lastMessageAt - a.lastMessageAt);
  },
);

export const hasChatSessions = createSelector(
  [getAllChatSessions],
  sessions => sessions && sessions.size > 0,
);

export const getActiveSession = createSelector(
  [getActiveSessionId, getSessionEntities],
  (activeSessionId, sessionEntities) => {
    return sessionEntities.get(activeSessionId, Session());
  },
);

export const getActiveSessionMessages = createSelector(
  [getActiveSession, getMessageEntities],
  (activeSession, messageEntities) => {
    return activeSession.messages.map(messageId => messageEntities.get(messageId));
  },
);

export const getActiveSessionUsers = createSelector(
  [getActiveSessionMessages, getUserEntities],
  (messages, userEntities) => {
    return messages.reduce((list, message) => {
      if (message.sentBy.type !== 'user') return list;

      const user = userEntities.get(message.sentBy.id);
      return user ? list.push(user) : list;
    }, Immutable.List());
  },
);

export const hasAgentInActiveSessionMessages = createSelector(
  [getActiveSessionMessages],
  messages =>
    messages &&
    messages.reduce((value, message) => {
      return value || (message.sentBy.type === 'user' && message.sentBy.id !== '__team');
    }, false),
);

export const hasCustomerInActiveSessionMessages = createSelector(
  [getActiveSessionMessages],
  messages =>
    messages &&
    messages.reduce((value, message) => {
      return value || message.sentBy.type === 'customer';
    }, false),
);

export const getUnreadMessageCounts = createSelector(
  [getSessionsWithoutMessages, getMessageEntities],
  (sessions, messageEntities) => {
    const countUnreadMessagesInSession = session => {
      return session.messages.reduce((total, messageId) => {
        const message = messageEntities.get(messageId, Message());
        return message.createdAt > session.lastSeenAt ? total + 1 : total;
      }, 0);
    };

    const mapSessionToUnreadCount = session => {
      const unreadCount = countUnreadMessagesInSession(session);
      return { sessionId: session.id, unreadCount };
    };

    return Immutable.fromJS(sessions.map(mapSessionToUnreadCount));
  },
);

export const getTotalUnreadMessageCount = createSelector(
  [getUnreadMessageCounts],
  unreadMessageCounts =>
    unreadMessageCounts.reduce((total, item) => {
      return total + item.unreadCount;
    }, 0),
);

export const getTotalUnreadMessageCountNotInActiveSession = createSelector(
  [getUnreadMessageCounts, getActiveSessionId],
  (unreadMessageCounts, sessionId) => {
    return unreadMessageCounts.reduce((total, item) => {
      if (item.sessionId === sessionId) return total;

      return total + item.unreadCount;
    }, 0);
  },
);

export const getTotalUnreadMessageCountInActiveSession = createSelector(
  [getUnreadMessageCounts, getActiveSessionId],
  (unreadMessageCounts = [], sessionId) => {
    return unreadMessageCounts.reduce((total, item) => {
      if (item.sessionId !== sessionId) return total;

      return total + item.unreadCount;
    }, 0);
  },
);

export const getCampaignMessages = createSelector(
  [getSessionsWithoutMessages, getMessageEntities],
  (sessions, messageEntities) => {
    if (!sessions || !messageEntities) return Immutable.OrderedSet();

    const getLastUnreadCampaignMessageInSession = session => {
      return session.messages.reduce((lastUnreadMessage, messageId) => {
        const message = messageEntities.get(messageId, Message());
        if (message.campaignId && message.createdAt > session.lastSeenAt) {
          return message;
        }

        return lastUnreadMessage;
      }, undefined);
    };

    const mapSessionToLastUnreadMessage = session => {
      const message = getLastUnreadCampaignMessageInSession(session);
      if (!message) return null;

      return { sessionId: session.id, message };
    };

    const messages = sessions.map(mapSessionToLastUnreadMessage).filter(s => s);
    return Immutable.fromJS(messages).take(3);
  },
);

export const selectCampaignMessageIncludesCustomerResponse = createSelector(
  getActiveSessionMessages,
  sessionMessages => {
    // todo this flippin line gets tests to pass
    const firstMessage =
      Immutable.fromJS(sessionMessages)
        .toMap()
        .first() || Message();

    if (!firstMessage.get('campaignId')) return undefined;

    if (firstMessage.get('campaignId')) {
      return sessionMessages.reduce((value, message) => {
        return value || message.sentBy.type === 'customer';
      }, false);
    }

    return false;
  },
);

export const selectActiveSessionHasCampaign = createSelector(
  getActiveSessionMessages,
  sessionMessages => {
    return Boolean(sessionMessages.find(message => message.campaignId));
  },
);

export const sessionContainsCampaign = createSelector(
  getSessionEntities,
  getMessageEntities,
  (_, props) => props.sessionId,
  (sessions, messages, sessionId) => {
    const session = sessions.get(sessionId);

    if (!session) return false;

    const sessionMessages = session.messages.map(messageId => messages.get(messageId));

    return Boolean(sessionMessages.find(message => message.campaignId));
  },
);

export const makeGetFirstMessageInSession = () => {
  return createSelector(
    [getSessionMessagesFromPropItemId, getMessageEntities],
    (messageList, messageEntities) => messageEntities.get(messageList.last()),
  );
};

export const makeGetUnreadCountsForSession = () => {
  return createSelector(
    [getSessionFromPropItemId, getMessageEntities],
    (session, messageEntities) =>
      session.messages.reduce((total, messageId) => {
        const message = messageEntities.get(messageId, Message());
        return message.createdAt > session.lastSeenAt ? total + 1 : total;
      }, 0),
  );
};

const getUsersInSession = (session, messageEntities, userEntities) => {
  return session.messages.reduce((list, message) => {
    const messageObject = messageEntities.get(message, Message());
    const sentById = messageObject.sentBy.id;
    const sentByType = messageObject.sentBy.type;
    if (sentByType !== 'user') return list;

    const user = userEntities.get(sentById);
    return user ? list.push(user) : list;
  }, Immutable.List());
};

export const makeGetSessionUserName = () => {
  return createSelector(
    [getSessionFromPropItemId, getMessageEntities, getUserEntities, getCompany],
    // eslint-disable-next-line max-params
    (session, messageEntities, userEntities, company) => {
      const lastUser = getUsersInSession(session, messageEntities, userEntities).last();
      if (lastUser && lastUser.name) return lastUser.name;

      return company.name;
    },
  );
};

export const makeGetSessionAvatarUrl = () => {
  return createSelector(
    [getSessionFromPropItemId, getMessageEntities, getUserEntities, getCompany],
    // eslint-disable-next-line max-params
    (session, messageEntities, userEntities, company) => {
      const lastUser = getUsersInSession(session, messageEntities, userEntities).last();
      if (lastUser && lastUser.avatarUrl) return lastUser.avatarUrl;

      return company.avatarUrl;
    },
  );
};

export const getActiveSessionSocketTypingActivity = createSelector(
  [getSocketActivity, getActiveSessionId],
  (activity, sessionId) => {
    const sessionActivity = activity.get(sessionId);

    if (!sessionActivity) return undefined;

    return sessionActivity.get('typingStatus');
  },
);

export const makeGetSessionLastMessageTimeUTC = () => {
  return createSelector(
    [getSessionFromPropItemId, getMessageEntities],
    (session, messageEntities) => {
      const lastMessage = messageEntities.get(session.messages.last(), Message());

      const lastMessageTimeUTC = get(lastMessage, 'createdAt');

      return lastMessageTimeUTC;
    },
  );
};

export const makeGetSessionLastMessage = () => {
  return createSelector(
    [getSessionFromPropItemId, getMessageEntities],
    (session, messageEntities) => {
      const messages = get(session, 'messages');

      if (!messageEntities || !messageEntities.size) return undefined;

      const idOfLastMessage = messages && messages.last();

      return messageEntities.get(idOfLastMessage, Message());
    },
  );
};

export const makeGetChatUserFromMessage = () => {
  return createSelector(
    [getUserFromMessageProp],
    user => user,
  );
};

export const getChatForm = state => {
  return state.getIn(['chat', 'entities', 'forms'], Form());
};

export const selectTeams = state => {
  return state.getIn(['chat', 'entities', 'teams']);
};

export const selectArticles = state => {
  return state.getIn(['chat', 'entities', 'articles']);
};

export const selectArticlesList = createSelector(
  [selectArticles],
  articles => {
    return articles.toArray();
  },
);

export const selectArticlesFetching = state => {
  return state.getIn(['chat', 'articlesFetching']);
};

export const selectTeamOptions = createSelector(
  selectTeams,
  teams =>
    teams
      .map(team => ({
        value: team.get('id'),
        displayName: team.get('displayName'),
        icon: team.get('icon'),
      }))
      .toArray(),
);

export const formHasEmailQuestion = state => {
  const form = getChatForm(state);
  const questions = Immutable.fromJS(form.get('questions'));
  if (!questions.size) return false;
  return Boolean(questions.find(q => q.get('property') === 'customer_email'));
};

export const selectIsAfterHours = state => {
  return state.getIn(['chat', 'isAfterHours']);
};

export const selectOfflineMessage = state => {
  return state.getIn(['chat', 'entities', 'company', 'offHoursMessage']);
};

export const selectOfflineImageUrl = state => {
  return state.getIn(['chat', 'entities', 'company', 'offhoursImageUrl']);
};

export const makeSelectMessageAttachment = () =>
  createSelector(
    getMessageEntities,
    getAttachmentEntities,
    (_, { message: { id } }) => id,
    (messages, attachments, messageId) => {
      const message = messages.get(messageId, Immutable.Map());

      if (message.attachments) {
        return attachments.get(messageId);
      }

      return null;
    },
  );

export const filterLockedSessions = createSelector(
  getSessionEntities,
  sessions => {
    return sessions.filter(session => !session.lockedAt);
  },
);

export const filterCampaignSessions = createSelector(
  filterLockedSessions,
  getMessageEntities,
  (sessions, messages) => {
    return sessions.filter(session => {
      return !session.messages.every(messagesId => {
        return messages.get(messagesId).campaignId;
      });
    });
  },
);

export const hasMoreThanOneActiveSession = createSelector(
  filterCampaignSessions,
  activeSessions => {
    return activeSessions.size > 1;
  },
);

export const getActiveSessionCount = createSelector(
  filterCampaignSessions,
  activeSessions => {
    return activeSessions.size;
  },
);

export const getMostRecentChatId = createSelector(
  filterLockedSessions,
  activeSessions => {
    const firstSession = activeSessions.first();

    if (!firstSession) return undefined;

    return firstSession.id;
  },
);

export const getSingleChatId = state => {
  return state.getIn(['chat', 'singleChat', 'sessionId']);
};

export const getSingleChatEnabled = state => {
  return state.getIn(['chat', 'singleChat', 'enabled']);
};

export const selectActiveSessionWaitTime = createSelector(
  getActiveSession,
  getQueueEntities,
  (activeSession, queues) => {
    const relatedQueue = queues.find(queue => queue.sessionId === activeSession.id);

    if (!relatedQueue) return null;

    return relatedQueue.estimatedWaitTime;
  },
);

export const selectSessionWaitTime = (state, sessionId) => {
  const queues = getQueueEntities(state);
  const relatedQueue = queues.find(queue => queue.sessionId === sessionId);

  if (!relatedQueue) return null;

  return relatedQueue.estimatedWaitTime;
};

export const getUpfrontVolumeControlEnabled = state => {
  const vcSetting = state.getIn(['chat', 'entities', 'company', 'volumeControl'], Immutable.Record());
  return vcSetting.mode === 'upfront';
};

export const getUpfrontVolumeControlThreshold = state => {
  const vcSetting = state.getIn(['chat', 'entities', 'company', 'volumeControl'], Immutable.Record());
  return vcSetting.upfrontWaitThreshold;
};

export const getSessionStates = state => state.getIn(['chat', 'sessionStates']);

export const makeGetSessionStateBySessionId = () => {
  return createSelector(
    [getSessionStates, (_, sessionId) => sessionId],
    (sessionStates, sessionId) => {
      if (!sessionStates) return null;

      return sessionStates[`session_${sessionId}`];
    },
  );
};

export const makeGetSessionStateSatisfactionBySessionId = () => {
  const getSessionStateBySessionId = makeGetSessionStateBySessionId();

  return createSelector(
    getSessionStateBySessionId,
    sessionState => {
      if (!sessionState) return null;

      return sessionState.satisfaction;
    },
  );
};

export const getSatisfactionForms = state => state.getIn(['chat', 'entities', 'satisfactionForms'], Immutable.Map());

export const makeGetSatisfactionFormByResponseId = () =>
  createSelector(
    [getSatisfactionForms, (_, responseId) => responseId],
    (satisfactionForms, responseId) => {
      return satisfactionForms.get(responseId) || SatisfactionForm();
    },
  );

export const getMessageEntitiesBySessionId = createSelector(
  [(_, sessionId) => sessionId, getMessageEntities],
  (sessionId, messageEntities) => {
    return (
      messageEntities &&
      messageEntities.filter(sessionMessage => {
        return sessionMessage.get('sessionId') === sessionId;
      })
    );
  },
);
